import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Pill } from './PillGroup.js';
import { useActivity, type ActivityRun } from '../hooks/useActivity.js';
import { Pressable } from './ui/Pressable.js';

// ── Spinner SVG ───────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" style={{ flexShrink: 0, display: 'inline-block' }}>
      <circle
        cx="5" cy="5" r="3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="6 16"
      >
        <animateTransform
          attributeName="transform"
          attributeType="XML"
          type="rotate"
          from="0 5 5"
          to="360 5 5"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

// ── Elapsed time formatter ────────────────────────────────────────────────────

function formatElapsed(startedAtMs: number): string {
  const s = Math.floor((Date.now() - startedAtMs) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// ── Display label helper ──────────────────────────────────────────────────────

/** Returns a localised display label for a run.
 *  When `run.kind` is set the frontend translates via activity.kind.<kind>.
 *  Falls back to `run.label` (pipeline name or legacy value). */
function getRunDisplayLabel(
  run: ActivityRun,
  t: TFunction,
): string {
  if (run.kind) {
    const count = run.progress?.total ?? 0;
    switch (run.kind) {
      case 'scrape_run':         return t('activity.kind.scrapeRun');
      case 'bulk_analyze':       return t('activity.kind.bulkAnalyze', { count });
      case 'bulk_draft':         return t('activity.kind.bulkDraft', { count });
      case 'bulk_send':          return t('activity.kind.bulkSend', { count });
      case 'report_generate':    return t('activity.kind.reportGenerate');
      case 'pipeline_not_found': return t('activity.kind.pipelineFallback', { id: run.pipelineId ?? '' });
    }
  }
  return run.label;
}

// ── Run row in popover ────────────────────────────────────────────────────────

function RunRow({ run, onNavigate }: { run: ActivityRun; onNavigate: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [, forceRender] = useState(0);

  // Tick every second to update elapsed time
  useEffect(() => {
    const id = setInterval(() => forceRender(n => n + 1), 1_000);
    return () => clearInterval(id);
  }, []);

  function handleClick() {
    navigate(run.linkTo);
    onNavigate();
  }

  const typeLabel = run.type === 'lead_scrape'
    ? `${t('activity.type.leadScrape')} · ${run.step ? t(`activity.step.${run.step}`, { defaultValue: run.step }) : t('activity.step.scrape')}`
    : run.type === 'manual_action'
      ? t('activity.type.manualAction')
      : run.type === 'ai_generation'
        ? t('activity.aiGeneration')
        : run.type === 'report_generate'
          ? t('activity.type.reportGenerate')
          : run.type;

  return (
    <Pressable fullWidth onPress={handleClick}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '10px 12px',
        textAlign: 'left',
      }}>
        {/* Label + elapsed */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
            {getRunDisplayLabel(run, t)}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', flexShrink: 0 }}>
            {formatElapsed(run.startedAt)}
          </span>
        </div>

        {/* Type + step */}
        <span style={{ fontSize: '11px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
          {typeLabel}
        </span>

        {/* Progress bar */}
        {run.progress && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
            <div
              style={{
                flex: 1,
                height: '3px',
                borderRadius: '2px',
                background: 'var(--evo-stroke)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, (run.progress.current / run.progress.total) * 100)}%`,
                  background: 'var(--evo-cyan)',
                  borderRadius: '2px',
                  transition: 'width 300ms',
                }}
              />
            </div>
            <span style={{ fontSize: '10px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', flexShrink: 0 }}>
              {run.progress.evaluated != null
                ? `${run.progress.evaluated} eval · ${run.progress.current}/${run.progress.total}`
                : `${run.progress.current}/${run.progress.total}`}
            </span>
          </div>
        )}
      </div>
    </Pressable>
  );
}

// ── Popover ───────────────────────────────────────────────────────────────────

function ActivityPopover({
  runs,
  anchorRef,
  onClose,
}: {
  runs: ActivityRun[];
  anchorRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Position below anchor
  const [pos, setPos] = useState({ top: 0, right: 0 });
  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
  }, [anchorRef]);

  // Close on click outside or Escape
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
          anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: pos.top,
        right: pos.right,
        width: '320px',
        maxHeight: '400px',
        overflowY: 'auto',
        background: 'var(--evo-panel)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r-md)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        zIndex: 800,
        padding: '4px',
      }}
    >
      {runs.map(run => (
        <RunRow key={run.id} run={run} onNavigate={onClose} />
      ))}
    </div>,
    document.body,
  );
}

// ── GlobalActivityPill ────────────────────────────────────────────────────────

export function GlobalActivityPill() {
  const { t } = useTranslation();
  const { isActive, totalRuns, runs } = useActivity();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Auto-close popover when activity stops
  useEffect(() => {
    if (!isActive) setOpen(false);
  }, [isActive]);

  if (!isActive) return null;

  // When there's exactly one active run, surface the current step on the pill
  // (e.g. "Scraping…", "Enriching…", "Sending…") so the user knows what phase
  // the pipeline is in. For multiple concurrent runs, fall back to the count.
  const singleRun = totalRuns === 1 ? runs[0] : null;
  // For a single run: prefer step i18n key → run label → generic count.
  // Virtual AI runs set step=null and label to the current progress string.
  const pillLabel = singleRun?.step
    ? t(`activity.step.${singleRun.step}`, { defaultValue: t('activity.processing', { count: totalRuns }) })
    : singleRun
      ? (getRunDisplayLabel(singleRun, t) || t('activity.processing', { count: totalRuns }))
      : t('activity.processing', { count: totalRuns });

  return (
    <div ref={anchorRef} style={{ flexShrink: 0 }}>
      <Pill
        tone="cyan"
        variant="subtle"
        size="sm"
        icon={<Spinner />}
        onClick={() => setOpen(o => !o)}
      >
        {pillLabel}
      </Pill>

      {open && (
        <ActivityPopover
          runs={runs}
          anchorRef={anchorRef}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
